import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Cube, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIERS = [
  { id: "starter", name: "Starter", monthly: 29, annual: 299, users: "1 user", tagline: "Solo builders" },
  { id: "business", name: "Business", monthly: 59, annual: 599, users: "Up to 3 users", tagline: "Small teams", popular: true },
  { id: "pro", name: "Pro", monthly: 99, annual: 1049, users: "Up to 10 users", tagline: "Growing firms" },
];

const FEATURES = [
  "Unlimited projects & phases",
  "Budget vs. actual + variance alerts",
  "AI budget extraction (PDF/scan/spreadsheet)",
  "Vendor directory & best-price comparison",
  "Quotation builder, PDF export & email",
  "Reusable Cost Library with price trends",
  "Excel/CSV import & export, weekly digests",
  "Activity audit trail & portfolio roll-up",
];

export default function Pricing() {
  const { subscription } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(true);
  const [loading, setLoading] = useState(null);
  const isManager = subscription?.is_manager;
  const currentPlan = subscription?.active && !subscription?.trial ? subscription?.plan : null;

  const subscribe = async (tier) => {
    if (!isManager) { toast.error("Only the team owner can manage billing"); return; }
    setLoading(tier);
    try {
      const { data } = await api.post("/billing/checkout", { tier, interval: annual ? "annual" : "monthly", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-heading font-black tracking-tighter" data-testid="pricing-logo">
            <Cube size={24} weight="bold" /> BuildTrack
          </button>
          <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="pricing-back">
            <ArrowLeft size={16} /> Back to app
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="max-w-2xl reveal-fade">
          <p className="label-caps mb-3">Pricing</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
            Plans that scale with your team.
          </h1>
          <p className="text-muted-foreground mt-4 text-base" data-testid="pricing-subtitle">
            {subscription?.trial
              ? `You're on a free trial — ${subscription.days_left} day${subscription.days_left === 1 ? "" : "s"} left (Business-level access). Pick a plan to keep going.`
              : currentPlan
              ? "You're subscribed. Switch tiers anytime — access extends on payment."
              : "Start with a 7-day free trial. Choose the tier that fits your crew."}
          </p>
        </div>

        {/* Interval toggle */}
        <div className="relative inline-block mt-8">
          <span data-testid="annual-ribbon" className="absolute -top-3 right-0 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border-2 border-[#0A0A0A]">
            Up to 2 months free
          </span>
          <div className="inline-flex border-2 border-[#0A0A0A] p-1" data-testid="interval-toggle">
            <button onClick={() => setAnnual(false)} data-testid="interval-monthly"
              className={`px-5 py-1.5 text-sm font-bold uppercase tracking-wider transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              Monthly
            </button>
            <button onClick={() => setAnnual(true)} data-testid="interval-annual"
              className={`px-5 py-1.5 text-sm font-bold uppercase tracking-wider transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              Annual <span className="text-emerald-600">save ~15%</span>
            </button>
          </div>
        </div>

        {/* Tier cards */}
        <div className="mt-10 grid md:grid-cols-3 gap-6 stagger">
          {TIERS.map((t) => {
            const isCurrent = currentPlan === t.id;
            const price = annual ? t.annual : t.monthly;
            return (
              <div key={t.id} data-testid={`tier-card-${t.id}`}
                className={`relative border-2 p-7 bg-white flex flex-col ${t.popular ? "border-[#F59E0B] shadow-[8px_8px_0_0_#F59E0B]" : "border-[#0A0A0A] shadow-[5px_5px_0_0_#0A0A0A]"}`}>
                {t.popular && <div className="absolute -top-3 left-7 bg-[#F59E0B] text-black text-xs font-bold uppercase tracking-wider px-2 py-0.5 border-2 border-[#0A0A0A]">Most popular</div>}
                <div className="font-heading font-black text-2xl">{t.name}</div>
                <div className="text-sm text-muted-foreground">{t.tagline}</div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-mono-fig text-4xl font-bold tabular-nums" data-testid={`tier-price-${t.id}`}>${price}</span>
                  <span className="text-muted-foreground text-sm">/{annual ? "yr" : "mo"}</span>
                </div>
                <div className="mt-2 inline-flex self-start label-caps border border-border px-2 py-0.5">{t.users}</div>
                {annual && (t.monthly * 12 - t.annual) > 0 && (
                  <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid={`tier-savings-${t.id}`}>
                    Save ${t.monthly * 12 - t.annual}/yr vs monthly
                  </p>
                )}
                {isCurrent ? (
                  <Button disabled className="w-full mt-5 rounded-none" data-testid={`current-${t.id}`}>Current plan ✓</Button>
                ) : (
                  <Button onClick={() => subscribe(t.id)} disabled={loading === t.id}
                    data-testid={`subscribe-${t.id}`}
                    className={`w-full mt-5 rounded-none h-11 ${t.popular ? "bg-[#F59E0B] text-black border-2 border-[#0A0A0A] hover:bg-[#F59E0B]/90" : ""}`}>
                    {loading === t.id ? "Redirecting…" : currentPlan ? "Switch to this" : "Start free trial"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Shared features */}
        <div className="mt-12 border-t border-border pt-8">
          <p className="label-caps mb-5">Every plan includes</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 w-5 h-5 bg-primary text-primary-foreground flex items-center justify-center"><Check size={13} weight="bold" /></span>
                <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-8 text-center">Secure checkout via Stripe · Cancel anytime · Prices in USD</p>
        </div>
      </div>
    </div>
  );
}
